import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from '@mui/icons-material/Search';
import { fetchReport, handleError, formatDateForMySQL, formatCurrency, formatDate2 } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useReportDatePickerLocale } from '../../hooks/useReportDatePickerLocale';

const MovementsReport = () => {
  const { t } = useTranslation();
  const { datePickerFormat, dayjsPickerLocale } = useReportDatePickerLocale();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setNavigation, state } = useNavigationHelperStore();
  
  // Date filters
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  
  // Load saved search parameters when returning to the page
  useEffect(() => {
    if (state) {
      const { startDate: savedStartDate, endDate: savedEndDate, page: savedPage } = state;
      if (savedStartDate) setStartDate(dayjs(savedStartDate));
      if (savedEndDate) setEndDate(dayjs(savedEndDate));
      if (savedPage) setPage(savedPage);
      handleSearch();
    }
  }, []);

  // Save search parameters when leaving this page
  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      page
    });
  };

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('MOVEMENTS', page, {
        from: formattedStartDate,
        to: formattedEndDate
      });
      
      setData(response);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePageChange = async (event, value) => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('MOVEMENTS', value, {
        from: formattedStartDate,
        to: formattedEndDate
      });
      
      setData(response);
      setPage(value);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMovementClick = (movement) => {
    // Save current state before navigating
    saveCurrentState();
    navigate('/main/view-movement', { state: { id: movement.id } });
  };

  const getTypeChip = (type) => {
    switch (type) {
      case 'PROCESSED':
        return <Chip label={t('reports.processedStatus')} color="success" size="small" />;
      case 'IN_TRANSIT':
        return <Chip label={t('reports.inTransitStatus')} color="warning" size="small" />;
      case 'PENDING':
        return <Chip label={t('reports.pendingStatus')} color="info" size="small" />;
      case 'CANCELLED':
        return <Chip label={t('reports.cancelledStatus')} color="error" size="small" />;
      default:
        return <Chip label={type || '-'} size="small" />;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <DatePicker
                label={t('common.from')}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format={datePickerFormat}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={5}>
              <DatePicker
                label={t('common.to')}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format={datePickerFormat}
                minDate={startDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <SearchIcon 
                onClick={handleSearch}
                sx={{ 
                  fontSize: 28, 
                  cursor: 'pointer',
                  color: isLoading ? 'text.disabled' : 'text.primary',
                  display: 'block',
                  margin: 'auto'
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2}>
                <Typography variant="h6">
                  {t('reports.movementsTitle')} ({data.total_results || data.content.length})
                </Typography>
              </Box>
              
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table sx={{ minWidth: 350 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.date')}</TableCell>
                      <TableCell>{t('common.receipt')}</TableCell>
                      <TableCell>{t('common.type')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.content.map((movement) => (
                      <TableRow 
                        key={movement.id}
                        hover
                        onClick={() => handleMovementClick(movement)}
                        sx={{ '&:hover': { cursor: 'pointer' } }}
                      >
                        <TableCell>{formatDate2(movement.date)}</TableCell>
                        <TableCell>{movement.receipt || '-'}</TableCell>
                        <TableCell>{getTypeChip(movement.type)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noInventoryMovements')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.selectDateRange')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default MovementsReport;